import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Compass, MapPin, Waves } from "lucide-react";
import { fetchVolunteers, fetchCommunityStats, fetchPulse } from "../lib/api";
import { timeAgo } from "../lib/tone";
import { OpportunityCard } from "../components/OpportunityCard";
import { WeekendPicks } from "../components/WeekendPicks";
import { HERO_HOME } from "../lib/volunteerVisuals";

const STATS = [["volunteer_opportunities", "Volunteer Opportunities", "/volunteer"], ["fundraisers", "Fundraisers", "/fundraisers"],
  ["events", "Community Events", "/events"], ["sources", "Community Sources", "/organizations"], ["total_interested", "Interested Actions", "/action-index"], ["total_attended", "Attended Actions", "/action-index"]];
const STEPS = [[Search, "Find a cause", "Food drives, tree plantings, hospitals, shelters, mentoring — find what matters to you."],
  [Compass, "Find your fit", "Volunteer roles, fundraisers and events filtered by cause and place."],
  [MapPin, "Take action locally", "Non-political, non-religious ways to help close to home."],
  [Waves, "Create a ripple", "Small actions add up to stronger communities, one action at a time."]];

const StatsCard = ({ s }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="home-live-stats">
    <p className="text-xs font-semibold uppercase tracking-widest text-sky-800">Live across Canada</p>
    <div className="mt-5 grid grid-cols-2 gap-5">
      {STATS.map(([k, l, to]) => (
        <Link key={l} to={to} className="group" data-testid={`home-stat-${l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <p className="text-3xl font-bold tracking-tight text-[#1E3A8A] group-hover:text-emerald-700">{s ? (s[k] ?? 0).toLocaleString("en-CA") : "—"}</p>
          <p className="mt-1 text-sm text-slate-700">{l}</p>
        </Link>
      ))}
    </div>
  </div>
);

export default function Home() {
  const [stats, setStats] = useState(null);
  const [opps, setOpps] = useState([]);
  const [pulse, setPulse] = useState(null);
  useEffect(() => {
    fetchCommunityStats().then(setStats).catch(() => {});
    fetchPulse({ limit: 3 }).then((r) => setPulse(r.items)).catch(() => setPulse([]));
    fetchVolunteers({ source: "curated" }).then((r) => setOpps([r[0], r[17], r[38]].filter(Boolean))).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden">
        <img src={HERO_HOME} alt="Canadians volunteering together in their community" className="absolute inset-0 h-full w-full object-cover" data-testid="home-hero-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/30" />
        <div className="absolute inset-0 bg-white/75 lg:hidden" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 lg:grid-cols-2 lg:py-24">
          <div className="fade-up">
            <p className="inline-flex items-center rounded-full bg-[#1E3A8A] px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white" data-testid="home-hero-eyebrow">Every Action Creates a Ripple</p>
            <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              <span className="uppercase text-[#1E3A8A]" data-testid="home-hero-headline">Be the <span className="text-emerald-700">Change.</span></span>
            </h1>
            <p className="mt-6 max-w-lg text-base font-medium leading-relaxed text-slate-800 md:text-lg">Discover local opportunities to volunteer, support community causes, attend events, and create positive change where you live.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/volunteer" data-testid="hero-create-ripple-cta" className="btn-green inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">Create a Ripple <ArrowRight size={16} /></Link>
              <Link to="/events" data-testid="hero-explore-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-800 hover:border-sky-900">Explore Opportunities</Link>
            </div>
          </div>
          <div className="fade-up delay-1"><StatsCard s={stats} /></div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="home-pulse-preview">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="text-xs font-semibold uppercase tracking-widest text-sky-800">Get Involved</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Ways to help in your community today</h2></div>
          <Link to="/pulse" className="inline-flex items-center gap-1 text-sm font-semibold text-sky-800" data-testid="home-pulse-link">See how you can help <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {pulse === null && [...Array(3)].map((_, i) => <div key={i} className="h-44 animate-pulse rounded-3xl bg-white" />)}
          {pulse?.map((s) => (
            <Link key={s.id} to={`/pulse?story=${s.id}`} className="card-rise flex flex-col rounded-3xl border border-slate-200 bg-white p-5 hover:shadow-md" data-testid={`home-pulse-${s.id}`}>
              <span className="text-xs font-semibold text-[#1E3A8A]">{s.pulse_category} · {timeAgo(s.published)}</span>
              <span className="mt-2 font-semibold leading-snug text-slate-900">{s.title}</span>
              {s.action_note && <span className="mt-2 text-sm text-slate-700"><span className="font-semibold text-emerald-700">What can I do? </span>{s.action_note}</span>}
            </Link>
          ))}
          {pulse?.length === 0 && <p className="text-sm text-slate-600 md:col-span-3">Community stories are being reviewed — check back shortly.</p>}
        </div>
      </section>

      <WeekendPicks />

      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-800">Community Impact Starts Here</p>
          <h2 className="mb-10 mt-3 text-3xl font-bold tracking-tight text-slate-900">Four steps to Be the Change</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="brand-promise">
            {STEPS.map(([Icon, t, d]) => (
              <div key={t} className="rounded-3xl bg-slate-50 p-8"><Icon className="text-sky-800" size={26} />
                <h3 className="mt-5 text-lg font-semibold text-slate-900">{t}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div><p className="text-xs font-semibold uppercase tracking-widest text-emerald-800">Create a Ripple Today</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Small Actions. Bigger Impact.</h2></div>
          <Link to="/volunteer" className="inline-flex items-center gap-1 text-sm font-medium text-sky-800" data-testid="home-all-opportunities-link">Volunteer Today <ArrowRight size={14} /></Link>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">{opps.map((o) => <OpportunityCard key={o.id} o={o} />)}</div>
      </section>
    </div>
  );
}
